<template>
  <div class="screen-monitor-container">
    <!-- 视频网格容器 -->
    <div class="video-grid-container">
      <div
        class="video-grid"
        :style="gridStyle"
      >
        <div
          v-for="(vehicle, index) in displayVehicles"
          :key="vehicle.id || `empty-${index}`"
          class="video-slot"
          :class="{ 'has-video': vehicle && vehicle.id }"
        >
          <template v-if="vehicle && vehicle.id">
            <!-- 视频播放区域 -->
            <div class="video-wrapper">
              <div class="view-position">
                {{ getPositionText(vehicle.currentPosition) }}
              </div>
              <video
                :ref="el => setVideoRef(vehicle.id, el)"
                :id="`video-${vehicle.id}`"
                class="video-player"
                muted
                playsinline
                controls
              ></video>
              <div v-if="videoLoading[vehicle.id]" class="loading-overlay">
                <span>加载中...</span>
              </div>
              <div v-if="videoError[vehicle.id]" class="error-overlay">
                <span>视频加载失败</span>
                <a-button size="small" @click="retryLoadVideo(vehicle)">重试</a-button>
              </div>
            </div>
            
            <!-- 车辆信息 -->
            <div class="vehicle-info">
              <div class="vehicle-plate">{{ vehicle.licenceNo || vehicle.vin || '-' }}</div>
              <div class="vehicle-detail">
                <span class="vehicle-type">{{ vehicle.deptName || '-' }}</span>
                <span class="vehicle-status-text">
                  <span class="status-dot" :class="vehicleOnlineStatus[vehicle.id] === 1 ? 'status-online' : 'status-offline'"></span>
                  {{ vehicleOnlineStatus[vehicle.id] === 1 ? '在线' : '离线' }}
                </span>
              </div>
            </div>
            
            <!-- 视角切换 -->
            <div class="view-switcher">
              <span
                v-for="pos in positions"
                :key="pos.value"
                class="view-btn"
                :class="{ active: vehicle.currentPosition === pos.value }"
                @click="switchView(vehicle, pos.value)"
              >{{ pos.label }}</span>
            </div>
          </template>
          <template v-else>
            <div class="empty-slot">
              <span>暂无车辆</span>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, reactive } from 'vue';
import { useRoute } from 'vue-router';
import flvjs from 'flv.js';
import { getScreenInfo } from '@/api/monitor/screen';
import { getStartPlay } from '@/api/video';
import { getVehicleOnlineStatus } from '@/api/realtime/vehicleStatus';

const route = useRoute();

// 屏幕信息
const screenInfo = ref({
  name: '',
  specification: '5x4'
});

// 车辆列表
const vehicleList = ref([]);

// 车辆在线状态
const vehicleOnlineStatus = reactive({});

// 视频相关
const videoRefs = ref({});
const players = ref({});
const videoLoading = reactive({});
const videoError = reactive({});
const loadingVehicles = ref(new Set());
const maxConcurrentPlayers = 4;

// 视角位置选项
const positions = [
  { label: '前', value: 'front' },
  { label: '后', value: 'back' },
  { label: '左', value: 'left' },
  { label: '右', value: 'right' }
];

// 视角文字映射
const positionMap = {
  'front': '前方',
  'back': '后方',
  'left': '左侧',
  'right': '右侧'
};

// 获取视角文字
const getPositionText = (position) => positionMap[position] || '前方';

// 根据屏幕规格计算网格样式
const gridStyle = computed(() => {
  const spec = screenInfo.value.specification || '5x4';
  const [cols, rows] = spec.split('x').map(Number);
  return {
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gridTemplateRows: `repeat(${rows}, 1fr)`
  };
});

// 需要显示的车辆列表（根据规格计算最大数量）
const displayVehicles = computed(() => {
  const spec = screenInfo.value.specification || '5x4';
  const [cols, rows] = spec.split('x').map(Number);
  const maxCount = cols * rows;
  const result = [...vehicleList.value];
  while (result.length < maxCount) {
    result.push({ id: null });
  }
  return result.slice(0, maxCount);
});

// 设置视频元素引用
const setVideoRef = (id, el) => {
  if (el) {
    videoRefs.value[id] = el;
  }
};

// 加载屏幕信息
const loadScreenInfo = async () => {
  const screenId = route.query.id;
  if (!screenId) return;

  try {
    const res = await getScreenInfo(screenId);
    if (res.code === 200 && res.data) {
      screenInfo.value = {
        name: res.data.name || '监控屏幕',
        specification: res.data.spec || '5x4'
      };
      
      // 直接使用接口返回的车辆列表
      if (res.data.vehicleList && res.data.vehicleList.length > 0) {
        vehicleList.value = res.data.vehicleList.map(v => ({
          ...v,
          id: v.vehicleId || v.id,
          currentPosition: 'front',
          videoUrls: {}
        }));
        
        // 获取车辆在线状态
        fetchVehicleOnlineStatus();
        
        // 初始化视频播放
        await nextTick();
        initAllVideos();
      }
    }
  } catch (error) {
    console.error('获取屏幕信息失败:', error);
  }
};

// 初始化所有视频
const initAllVideos = async () => {
  for (const vehicle of vehicleList.value) {
    await nextTick();
    initVideoForVehicle(vehicle);
  }
};

// 获取车辆在线状态
const fetchVehicleOnlineStatus = async () => {
  try {
    for (const vehicle of vehicleList.value) {
      if (vehicle.id) {
        const res = await getVehicleOnlineStatus(vehicle.id);
        if (res.code === 200) {
          vehicleOnlineStatus[vehicle.id] = res.data;
        }
      }
    }
  } catch (error) {
    console.error('获取车辆在线状态失败:', error);
  }
};

// 为单个车辆初始化视频
const initVideoForVehicle = async (vehicle) => {
  const vehicleId = vehicle.id;
  
  if (loadingVehicles.value.has(vehicleId)) return;
  
  // 检查并发数
  if (loadingVehicles.value.size >= maxConcurrentPlayers) {
    setTimeout(() => initVideoForVehicle(vehicle), 1000);
    return;
  }
  
  loadingVehicles.value.add(vehicleId);
  videoLoading[vehicleId] = true;
  videoError[vehicleId] = false;
  
  try {
    const response = await getStartPlay({
      vehicleId: vehicleId,
      position: vehicle.currentPosition || 'front'
    });
    
    if (response.code === 200 && response.data) {
      vehicle.videoUrls = vehicle.videoUrls || {};
      vehicle.videoUrls[vehicle.currentPosition] = response.data;
      await initVideoPlayer(vehicleId, response.data);
    } else {
      videoError[vehicleId] = true;
    }
  } catch (error) {
    console.error(`获取车辆 ${vehicle.licenceNo} 视频流失败:`, error);
    videoError[vehicleId] = true;
  } finally {
    loadingVehicles.value.delete(vehicleId);
    videoLoading[vehicleId] = false;
  }
};

// 初始化视频播放器
const initVideoPlayer = async (vehicleId, url) => {
  await nextTick();
  const videoElement = videoRefs.value[vehicleId];
  if (!videoElement) {
    console.warn(`车辆 ${vehicleId} 的视频元素未准备好`);
    return;
  }
  
  // 销毁旧的播放器实例
  if (players.value[vehicleId]) {
    try {
      players.value[vehicleId].pause();
      players.value[vehicleId].unload();
      players.value[vehicleId].detachMediaElement();
      players.value[vehicleId].destroy();
    } catch (e) {
      console.warn(`销毁车辆 ${vehicleId} 播放器时出错:`, e);
    }
    players.value[vehicleId] = null;
  }
  
  if (!flvjs.isSupported()) {
    console.error('当前浏览器不支持 flv.js');
    videoElement.src = '';
    return;
  }
  
  try {
    const player = flvjs.createPlayer({
      type: 'flv',
      isLive: true,
      url: url,
      cors: true,
      hasAudio: false,
      hasVideo: true,
      headers: {
        'Referer': window.location.origin
      }
    }, {
      enableWorker: false,
      enableStashBuffer: false,
      stashInitialSize: 128,
      lazyLoad: true,
      lazyLoadMaxDuration: 3,
      autoCleanupSourceBuffer: true,
      fixAudioTimestampGap: true,
      isLive: true,
      referrerPolicy: 'no-referrer-when-downgrade'
    });
    
    player.attachMediaElement(videoElement);
    
    player.on(flvjs.Events.ERROR, (errorType, errorDetail) => {
      console.error(`车辆 ${vehicleId} 播放出错:`, errorType, errorDetail);
      videoError[vehicleId] = true;
    });
    
    player.load();
    player.play().catch(err => {
      console.warn(`车辆 ${vehicleId} 视频播放失败:`, err);
      setTimeout(() => {
        if (player) {
          player.play().catch(e => {
            console.error(`车辆 ${vehicleId} 重试播放失败:`, e);
          });
        }
      }, 1000);
    });
    
    players.value[vehicleId] = player;
  } catch (error) {
    console.error(`车辆 ${vehicleId} 初始化播放器失败:`, error);
    videoError[vehicleId] = true;
  }
};

// 切换视角
const switchView = async (vehicle, position) => {
  if (vehicle.currentPosition === position) return;
  
  const vehicleId = vehicle.id;
  vehicle.currentPosition = position;
  
  if (vehicle.videoUrls && vehicle.videoUrls[position]) {
    await initVideoPlayer(vehicleId, vehicle.videoUrls[position]);
    return;
  }
  
  if (loadingVehicles.value.has(vehicleId)) return;
  
  loadingVehicles.value.add(vehicleId);
  videoLoading[vehicleId] = true;
  videoError[vehicleId] = false;
  
  try {
    const response = await getStartPlay({
      vehicleId: vehicleId,
      position: position
    });
    
    if (response.code === 200 && response.data) {
      vehicle.videoUrls = vehicle.videoUrls || {};
      vehicle.videoUrls[position] = response.data;
      await initVideoPlayer(vehicleId, response.data);
    }
  } catch (error) {
    console.error(`切换车辆 ${vehicle.licenceNo} 视角失败:`, error);
    videoError[vehicleId] = true;
  } finally {
    loadingVehicles.value.delete(vehicleId);
    videoLoading[vehicleId] = false;
  }
};

// 重试加载视频
const retryLoadVideo = (vehicle) => {
  videoError[vehicle.id] = false;
  initVideoForVehicle(vehicle);
};

// 刷新所有视频
const refreshAllVideos = async () => {
  // 清理所有播放器
  for (const vehicleId in players.value) {
    if (players.value[vehicleId]) {
      try {
        players.value[vehicleId].pause();
        players.value[vehicleId].unload();
        players.value[vehicleId].detachMediaElement();
        players.value[vehicleId].destroy();
      } catch (e) {
        // 忽略错误
      }
      players.value[vehicleId] = null;
    }
  }
  
  // 重新初始化
  await nextTick();
  initAllVideos();
};

// 在线状态定时器
let onlineStatusTimer = null;

onMounted(() => {
  loadScreenInfo();
  // 每30秒更新一次车辆在线状态
  onlineStatusTimer = setInterval(() => {
    if (vehicleList.value.length > 0) {
      fetchVehicleOnlineStatus();
    }
  }, 30 * 1000);
});

onUnmounted(() => {
  // 清理定时器
  if (onlineStatusTimer) {
    clearInterval(onlineStatusTimer);
    onlineStatusTimer = null;
  }
  // 清理所有播放器实例
  for (const vehicleId in players.value) {
    if (players.value[vehicleId]) {
      try {
        players.value[vehicleId].pause();
        players.value[vehicleId].unload();
        players.value[vehicleId].detachMediaElement();
        players.value[vehicleId].destroy();
      } catch (e) {
        // 忽略错误
      }
      players.value[vehicleId] = null;
    }
  }
});
</script>

<style scoped lang="less">
.screen-monitor-container {
  width: 100%;
  height: calc(100vh - 70px);
  background: #16162a;
  display: flex;
  flex-direction: column;
}

.video-grid-container {
  flex: 1;
  padding: 16px;
  overflow: hidden;
}

.video-grid {
  display: grid;
  gap: 12px;
  width: 100%;
  height: 100%;
}

.video-slot {
  background: #16213e;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  position: relative;
}

.video-wrapper {
  flex: 1;
  position: relative;
  min-height: 0;
  
  .view-position {
    position: absolute;
    top: 8px;
    left: 8px;
    background: rgba(0, 0, 0, 0.6);
    padding: 4px 12px;
    border-radius: 4px;
    color: #fff;
    font-size: 12px;
    font-weight: 500;
    z-index: 10;
  }
  
  .loading-overlay, .error-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    gap: 12px;
  }
}

.video-player {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
}

.vehicle-info {
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .vehicle-plate {
    font-size: 14px;
    font-weight: bold;
    color: #fff;
  }
  
  .vehicle-detail {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .vehicle-type {
      font-size: 12px;
      color: #a0a0a0;
    }
    
    .vehicle-status-text {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: #a0a0a0;
      
      .status-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        
        &.status-online {
          background-color: #52c41a;
        }
        
        &.status-offline {
          background-color: #999;
        }
      }
    }
  }
}

.view-switcher {
  display: flex;
  background: rgba(0, 0, 0, 0.4);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  .view-btn {
    flex: 1;
    padding: 6px 0;
    text-align: center;
    font-size: 12px;
    color: #a0a0a0;
    cursor: pointer;
    transition: all 0.2s;
    
    &:hover {
      color: #fff;
      background: rgba(255, 255, 255, 0.1);
    }
    
    &.active {
      color: #1890ff;
      background: rgba(24, 144, 255, 0.2);
    }
  }
}

.empty-slot {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 14px;
}
</style>